import React from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, FileWarning, HelpCircle, Clock, Search } from 'lucide-react';
export function ProblemSection() {
  const problems = [{
    icon: MessageSquare,
    text: '800 mensajes al mes. Nadie tiene tiempo de leerlos.'
  }, {
    icon: FileWarning,
    text: 'Las decisiones importantes se entierran entre fotos y memes.'
  }, {
    icon: HelpCircle,
    text: 'El mandante pide explicación porque no entiende qué pasó esta semana.'
  }, {
    icon: Clock,
    text: 'Armar el reporte semanal toma 2 horas. Mínimo.'
  }, {
    icon: Search,
    text: 'Cuando alguien pregunta qué se decidió el martes 12, nadie sabe.'
  }];
  return <section id="problema" className="py-14 sm:py-20 bg-white" data-id="element-249">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-250">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-251">
          {/* Left column - Content */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-252">
            <div className="inline-block px-4 py-1.5 bg-blue-50 text-blue-600 text-xs font-bold uppercase tracking-wider rounded-full mb-4" data-id="element-253">
              El problema
            </div>

            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-6 leading-tight" data-id="element-254">
              El chat de tu obra tiene todo. Y al mismo tiempo, nada.
            </h2>

            <div className="space-y-4 mb-8" data-id="element-255">
              {problems.map((problem, index) => {
              const Icon = problem.icon;
              return <motion.div key={index} initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.5,
                delay: index * 0.1
              }} className="flex items-start gap-3 p-4 rounded-xl bg-gray-50 border border-gray-100" data-id="element-256">
                    <Icon className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" data-id="element-257" />
                    <p className="text-gray-700 font-medium" data-id="element-258">{problem.text}</p>
                  </motion.div>;
            })}
            </div>

            <motion.div initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.5
          }} className="inline-block px-5 py-3 bg-blue-50 border-2 border-blue-200 rounded-xl" data-id="element-259">
              <p className="text-sm font-bold text-blue-900" data-id="element-260">
                Tu obra ya está documentada. Solo no la puedes usar.
              </p>
            </motion.div>
          </motion.div>

          {/* Right column - Chaos illustration */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative" data-id="element-261">
            <div className="relative bg-gradient-to-br from-gray-100 to-gray-50 rounded-2xl p-8 border border-gray-200 shadow-lg overflow-hidden" data-id="element-262">
              {/* Chaotic photos */}
              <div className="relative h-[400px]" data-id="element-263">
                {/* Scattered photo placeholders */}
                <div className="absolute top-4 left-4 w-32 h-32 bg-gray-300 rounded-lg shadow-md transform -rotate-12 opacity-80" data-id="element-264" />
                <div className="absolute top-12 right-8 w-28 h-28 bg-gray-300 rounded-lg shadow-md transform rotate-6 opacity-70" data-id="element-265" />
                <div className="absolute bottom-20 left-12 w-36 h-36 bg-gray-300 rounded-lg shadow-md transform rotate-12 opacity-75" data-id="element-266" />
                <div className="absolute bottom-8 right-4 w-32 h-32 bg-gray-300 rounded-lg shadow-md transform -rotate-6 opacity-80" data-id="element-267" />
                <div className="absolute top-32 left-1/2 -translate-x-1/2 w-28 h-28 bg-gray-300 rounded-lg shadow-md transform rotate-3 opacity-65" data-id="element-268" />

                {/* Chat bubbles overlay */}
                <div className="absolute top-1/4 left-1/4 bg-white rounded-2xl rounded-bl-none px-4 py-2 shadow-lg max-w-[180px]" data-id="element-269">
                  <p className="text-xs text-gray-600" data-id="element-270">
                    ¿Alguien tiene la foto de la losa del martes?
                  </p>
                </div>

                <div className="absolute bottom-1/3 right-1/4 bg-blue-600 text-white rounded-2xl rounded-br-none px-4 py-2 shadow-lg max-w-[160px]" data-id="element-271">
                  <p className="text-xs" data-id="element-272">Creo que la mandó Juan...</p>
                </div>

                <div className="absolute top-1/2 left-1/3 bg-white rounded-2xl rounded-bl-none px-4 py-2 shadow-lg max-w-[140px]" data-id="element-273">
                  <p className="text-xs text-gray-600" data-id="element-274">¿Cuál Juan? 😅</p>
                </div>

                {/* Chaos indicators */}
                <div className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full" data-id="element-275">
                  847 mensajes
                </div>
              </div>

              {/* Quote at bottom */}
              <div className="mt-6 pt-6 border-t border-gray-300" data-id="element-276">
                <p className="text-sm text-gray-600 italic text-center" data-id="element-277">
                  "¿Dónde está la foto de la losa del martes?"
                </p>
              </div>
            </div>

            {/* Decorative blur */}
            <div className="absolute -bottom-8 -left-8 w-48 h-48 bg-red-200/30 rounded-full blur-3xl -z-10" data-id="element-278" />
          </motion.div>
        </div>
      </div>
    </section>;
}